import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface Account {
  code: string;
  name: string;
  category: string;
}

const accounts: Account[] = [
  { code: "1000", name: "Kasse", category: "Assets" },
  { code: "1020", name: "PostFinance", category: "Assets" },
  { code: "1100", name: "Debitoren", category: "Assets" },
  { code: "2100", name: "Kreditkarten", category: "Liabilities" },
  { code: "4300", name: "Office Supplies", category: "Expenses" },
  { code: "6210", name: "Telecom", category: "Expenses" },
  { code: "6400", name: "Marketing", category: "Expenses" },
  { code: "6510", name: "Travel Expenses", category: "Expenses" },
  { code: "6600", name: "IT Services", category: "Expenses" },
  { code: "8000", name: "Sales Revenue", category: "Revenue" },
];

interface AccountSelectorProps {
  value: string;
  onChange: (account: string) => void;
  placeholder?: string;
}

export const AccountSelector = ({ value, onChange, placeholder = "Select account..." }: AccountSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAccounts = accounts.filter(account =>
    account.code.includes(searchTerm) || 
    account.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedAccounts = filteredAccounts.reduce((groups, account) => {
    const category = account.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(account);
    return groups;
  }, {} as Record<string, Account[]>);

  const selectedAccount = accounts.find(acc => `${acc.code} - ${acc.name}` === value);

  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-xs bg-white border-brand-border">
        <SelectValue placeholder={placeholder}>
          {selectedAccount && (
            <span className="px-2 py-1 bg-gray-100 rounded text-xs">
              {selectedAccount.code} - {selectedAccount.name}
            </span>
          )}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="bg-white border border-brand-border shadow-lg z-50">
        <div className="p-2">
          <input
            type="text"
            placeholder="Search accounts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-8 px-3 bg-brand-secondary border border-brand-border rounded text-xs focus:outline-none focus:ring-2 focus:ring-brand-accent"
          />
        </div>
        
        {Object.entries(groupedAccounts).map(([category, categoryAccounts]) => (
          <div key={category}>
            <div className="px-3 py-2 text-xs font-medium text-brand-text-secondary border-b border-brand-border bg-brand-secondary">
              {category}
            </div>
            {categoryAccounts.map((account) => (
              <SelectItem 
                key={`${account.code}-${account.name}`}
                value={`${account.code} - ${account.name}`}
                className="text-xs hover:bg-brand-secondary cursor-pointer"
              >
                <div className="flex items-center justify-between w-full">
                  <span className="font-medium">{account.code}</span>
                  <span className="text-brand-text-secondary">{account.name}</span>
                </div>
              </SelectItem>
            ))}
          </div>
        ))}
        
        {filteredAccounts.length === 0 && (
          <div className="p-3 text-xs text-brand-text-secondary text-center">
            No accounts found
          </div>
        )}
      </SelectContent>
    </Select>
  );
};